import axios from 'axios'

const readToken = () => JSON.parse(localStorage.getItem('user') || '{}')?.state?.user?.token

const instance = axios.create({
	baseURL: import.meta.env.VITE_API_BASE_URL,
	headers: {
		Authorization: `Bearer ${readToken()}`,
	},
})

// Token update helper is defined below

const setTokenInStorage = (newToken) => {
	try {
		const raw = localStorage.getItem('user')
		if (!raw) return
		const obj = JSON.parse(raw)
		if (!obj.state) obj.state = {}
		if (!obj.state.user) obj.state.user = {}
		obj.state.user.token = newToken
		localStorage.setItem('user', JSON.stringify(obj))
	} catch {
		// if storage is corrupted, clear it
		localStorage.removeItem('user')
	}
}

// Attach token to all requests
instance.interceptors.request.use(
	(config) => {
		const accessToken = readToken()
		if (accessToken) {
			config.headers.Authorization = `Bearer ${accessToken}`
		}
		return config
	},
	(error) => Promise.reject(error),
)

// Handle server-driven token rotation + simple 401 handling (no client-side renew calls)
instance.interceptors.response.use(
	(response) => {
		// If backend rotated the token, it may send it via:
		// - Authorization: Bearer <token>
		// - x-access-token or x-auth-token
		// - response body (e.g., { data: { token } } or { token })
		try {
			const headers = response?.headers || {}
			// Header names may be lowercased by axios/node
			const authorization = headers['authorization'] || headers['Authorization']

			let rotatedToken = null
			if (authorization && typeof authorization === 'string') {
				const lower = authorization.toLowerCase()
				if (lower.startsWith('bearer ')) {
					rotatedToken = authorization.slice(7).trim()
				}
			}

			if (!rotatedToken) {
				const headerToken = headers['x-access-token'] || headers['x-auth-token']
				if (headerToken && typeof headerToken === 'string') {
					rotatedToken = headerToken
				}
			}

			if (!rotatedToken) {
				const data = response?.data
				rotatedToken = data?.data?.token || data?.token || data?.data?.user?.token || data?.user?.token || null
			}

			if (rotatedToken && typeof rotatedToken === 'string' && rotatedToken.length > 0) {
				setTokenInStorage(rotatedToken)
			}
		} catch {
			// ignore token parsing/storage errors to not break normal flow
		}

		return response
	},
	(error) => {
		const status = error?.response?.status
		if (status === 401) {
			// Unauthorized: clear local session and reload app
			localStorage.removeItem('user')
			window.location.reload()
		}
		return Promise.reject(error)
	},
)

export default instance
